import roleOptions from "../config/roleOptions.json";

/**
 * Merges a role schema with permission values and form data.
 * - Filters schema items based on "depends" conditions (dynamic visibility).
 * - For each schema item, finds the corresponding value item by module.
 * - If no value item exists, defaults all values (parent and children) to false.
 * - If value item exists, merges child permissions by key, preserving checked state.
 * - Returns a new schema array with merged values for rendering or saving.
 *
 * @param {Array} schema - The role schema definition (from JSON).
 * @param {Array} values - The current permission values (from DB or form).
 * @param {Object} formData - The current form data (for "depends" logic).
 * @returns {Array} The merged schema with checked/unchecked values.
 */
const mergeSchemaWithValues = (schema, values, formData) => {
    return schema
        .filter((schemaItem) => {
            // Check if the depends condition is met
            const { depends } = schemaItem;
            if (depends) {
                const { keys, value } = depends;
                return formData[keys] === value;
            }
            // Include item if no depends condition exists
            return true;
        })
        .map((schemaItem) => {
            // Find the corresponding item in values
            const valueItem = values.find((v) => v.module === schemaItem.module);

            // If not found, create a new one with default values
            if (!valueItem) {
                return {
                    ...schemaItem,
                    value: false,
                    children: schemaItem.children.map((child) => ({
                        ...child,
                        value: false,
                    })),
                };
            }

            // Merge children
            const mergedChildren = schemaItem.children.map((child) => {
                const valueChild = valueItem.children.find((c) => c.key === child.key);
                return {
                    ...child,
                    value: valueChild ? valueChild.value : false,
                };
            });

            // Return the merged item
            return {
                ...schemaItem,
                value: valueItem.value ?? false,
                children: mergedChildren,
            };
        });
};

/**
 * Returns merged role options for a section, given permission values and form data.
 * - Looks up the schema for the section from roleOptions JSON.
 * - Calls mergeSchemaWithValues to merge with current values and form state.
 *
 * @param {string} section - The section name in roleOptions.
 * @param {Array} permissionValues - The current permission values.
 * @param {Object} formData - The current form data.
 * @returns {Array} The merged role options for the section.
 */
export const mergedRoleOptions = (section, permissionValues, formData) => {

    return mergeSchemaWithValues(roleOptions[section], permissionValues, formData)
};

/**
 * Updates all parent and child values in a schema to the given value (true/false).
 * - Used for "select all" or "deselect all" functionality.
 *
 * @param {Array} schema - The role schema array.
 * @param {boolean} value - The value to set (true/false).
 * @returns {Array} The updated schema with all values set.
 */
export const updateParentAndChildrenValues = (schema, value) => {
    return schema.map((schemaItem) => ({
        ...schemaItem,
        value, // Set the parent value
        children: schemaItem.children.map((child) => ({
            ...child,
            value, // Set the child value
        })),
    }));
};